import { Dispatch, SetStateAction, useEffect } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
	DocumentFieldFilter,
	DocumentLinkTarget,
} from '@dealstack/frontend/shared/dataExchange/services/shared/types';
import { TableOperations } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting';
import { DocumentService } from '@dealstack/frontend/shared/dataExchange/services/document/document.service';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { formatUnicorn } from '@dealstack/shared/lib/format/formatUnicorn';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import { buildDocumentsQueryKey } from '@dealstack/frontend/shared/components/Documents/hooks/utils/buildDocumentsQueryKey';

interface Props {
	dealId: string;
	target: DocumentLinkTarget;
	dataOperations: TableOperations<DocumentFieldFilter>;
	setClassifierFilters: Dispatch<SetStateAction<string[]>>;
	setHasDocuments: Dispatch<SetStateAction<boolean>>;
}

export const useDocuments = ({
	dealId,
	target,
	dataOperations,
	setClassifierFilters,
	setHasDocuments,
}: Props) => {
	const tCapManage = useTranslationCapManage();

	const queryKey = buildDocumentsQueryKey({
		target,
		dataOperations,
	});

	const q = useQuery({
		queryKey: [formatUnicorn(API_PATHS.DEAL_DEALID_DOCUMENT_GET, { dealId }), queryKey],
		queryFn: ({ signal }) =>
			DocumentService.getDocuments(
				formatUnicorn(API_PATHS.DEAL_DEALID_DOCUMENT_GET, { dealId }),
				queryKey,
				signal
			),
		// This property is really really important for the table data, if it's excluded the table won't function properly
		// when it's false the filters reset when an update is made which is not ideal
		placeholderData: keepPreviousData,
		// Error toast is surfaced by the global QueryCache handler (see queryErrorHandler).
		meta: { errorToast: tCapManage('documents.errors.find-all') },
	});

	// classifierFilters feeds the table columns, which feed dataOperations, which
	// feed this query's key — so it's a genuine render-feedback cycle, not a
	// gratuitous sync. State here is intentional (it breaks the cycle).
	useEffect(() => {
		if (!q.isSuccess) {
			return;
		}
		const results = q.data;
		const hasData = !!results?.payload;
		if (results?.payload?.meta.classifiers) {
			setClassifierFilters(results.payload.meta.classifiers);
		}

		setHasDocuments(hasData);
	}, [q.isSuccess, q.data]);

	return q;
};
